import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Table, Column, Spin } from './common/Ant'
import { H3, H4 } from './common/Headers'
import Divider from './common/Divider'
import { dateTimeFormatter } from '../utils/date'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import { ROLES, SMOOTH_INTERVAL } from '../constants'
import DateRange from './common/DateRange'
import { hasRole } from '../utils/hasPermission'
import { toTitleCase } from '../utils/textFormatters'
import {
  CUSTOM_TIME_RANGE,
  DEFAULT_TIME_RANGE,
  getPresetWindow,
  getCurrentWindow,
  getTimeRangeFromParams,
  getTimeRangeParams,
  getSmoothQuery,
} from '../utils/timeRange'
import Select, { Option } from './common/Select'
import TimeRangeSelect from './common/TimeRangeSelect'
import FlowHistoryChart from './charts/FlowHistoryChart'
import FlowMonitorChart from './charts/FlowMonitorChart'

const POLL_INTERVAL_MS = 30000

class FlowMonitorData extends Component {
  static propTypes = {
    updateQueryParams: PropTypes.func.isRequired,
    flowMonitorData: PropTypes.object.isRequired,
    getFlowMonitorData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isByLocation: PropTypes.bool,
    pillarId: PropTypes.string.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      timeRange: DEFAULT_TIME_RANGE,
      ...getPresetWindow(DEFAULT_TIME_RANGE),
      smoothInterval: SMOOTH_INTERVAL.RAW,
    }
  }

  static defaultProps = {
    flowMonitorData: {},
  }

  componentDidMount() {
    const searchParams = parseQueryParams(global.location)
    const { timeRange, ...timeWindow } = getTimeRangeFromParams(searchParams)

    let { smoothInterval } = this.state
    if (searchParams.smoothInterval || searchParams.smooth) {
      smoothInterval = searchParams.smoothInterval
        ? searchParams.smoothInterval
        : !searchParams.smooth || searchParams.smooth === 't'
        ? SMOOTH_INTERVAL.ONE_HOUR
        : SMOOTH_INTERVAL.RAW
    }

    this.setState({ timeRange, ...timeWindow, smoothInterval })
    this.fetchData({ ...timeWindow, smoothInterval })

    this.pollTimer = setInterval(this.poll, POLL_INTERVAL_MS)
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer)
  }

  getLocationQuery = () =>
    this.props.isByLocation ? { location: 'location' } : {}

  fetchData = ({ startValue, endValue, smoothInterval }) => {
    const { getFlowMonitorData, pillarId } = this.props
    const qs = createQueryString({
      startTime: startValue.unix(),
      endTime: endValue.unix(),
      ...getSmoothQuery(smoothInterval),
      ...this.getLocationQuery(),
    })

    getFlowMonitorData(pillarId, qs)
  }

  updateUrl = ({ smoothInterval, ...timeRangeState }) => {
    const qs = createQueryString({
      ...getTimeRangeParams(timeRangeState),
      ...getSmoothQuery(smoothInterval),
      ...this.getLocationQuery(),
    })

    this.props.updateQueryParams({
      pathname: global.location.pathname,
      search: qs,
    })
  }

  poll = () => {
    const { timeRange, smoothInterval } = this.state

    if (timeRange === CUSTOM_TIME_RANGE) {
      return
    }

    const timeWindow = getPresetWindow(timeRange)

    this.setState(timeWindow)
    this.fetchData({ ...timeWindow, smoothInterval })
  }

  handleTimeRangeChange = timeRange => {
    const { smoothInterval } = this.state

    // switching to custom starts from whatever window is on screen
    const timeWindow = getCurrentWindow({ ...this.state, timeRange })

    this.setState({ timeRange, ...timeWindow })
    this.updateUrl({ timeRange, ...timeWindow, smoothInterval })

    if (timeRange !== CUSTOM_TIME_RANGE) {
      this.fetchData({ ...timeWindow, smoothInterval })
    }
  }

  onCloseDateRange = (startValue, endValue) => {
    const { timeRange, smoothInterval } = this.state

    if (!startValue?.isValid() || !endValue?.isValid()) {
      console.warn('Invalid date range values received:', {
        startValue,
        endValue,
      })
      return
    }

    this.setState({ startValue, endValue })
    this.updateUrl({ timeRange, startValue, endValue, smoothInterval })
    this.fetchData({ startValue, endValue, smoothInterval })
  }

  handleFilterChange = smoothInterval => {
    const { timeRange } = this.state
    const timeWindow = getCurrentWindow(this.state)

    this.setState({ smoothInterval, ...timeWindow })
    this.updateUrl({ timeRange, ...timeWindow, smoothInterval })
    this.fetchData({ ...timeWindow, smoothInterval })
  }

  render() {
    const { isLoading, flowMonitorData, updateQueryParams } = this.props
    const { timeRange, startValue, endValue, smoothInterval } = this.state

    const lastTime =
      flowMonitorData && flowMonitorData.lastData ? (
        <div className="flex">
          <div className="b">Last Flow Reading</div>
          <span className="ml3 f5 fw4">
            {dateTimeFormatter(flowMonitorData.lastData.time)}
          </span>
        </div>
      ) : (
        'Last Flow Reading'
      )

    const hasData = flowMonitorData && Object.keys(flowMonitorData).length > 0

    return isLoading && !hasData ? (
      <Spin size="large" className="w-100 center mt5" />
    ) : (
      <Fragment>
        <div className="mb3" />
        <H3>{lastTime}</H3>
        {flowMonitorData &&
        flowMonitorData.tableData &&
        flowMonitorData.tableData.length ? (
          <div className="flex-ns">
            <div className="w-40-ns mr5-ns">
              <FlowMonitorChart
                flowValue={
                  flowMonitorData &&
                  flowMonitorData.lastData &&
                  flowMonitorData.lastData.flowGpm
                    ? flowMonitorData.lastData.flowGpm.toString()
                    : '0.0'
                }
                maxFlow={flowMonitorData ? flowMonitorData.maxFlow : 150}
                minFlow={flowMonitorData ? flowMonitorData.minFlow : 0}
              />
            </div>
            <div className="w-60-ns mt4-ns">
              <Table
                dataSource={flowMonitorData.tableData}
                rowKey="key"
                scroll={{ x: 400 }}
                pagination={false}
                tableLayout="auto"
                bordered
              >
                <Column
                  key="name"
                  title="Recent Water Flow"
                  width={200}
                  dataIndex="name"
                  render={(text, record) => (text ? text : '--')}
                />
                <Column
                  key="avgValue"
                  title="Average Value - gal/min"
                  width={200}
                  dataIndex="avgValue"
                  render={(text, record) => (text ? text : '--')}
                />
              </Table>
              <H4>Monthly Usage Estimate: {flowMonitorData.monthlyUsageEstimationGallons ? flowMonitorData.monthlyUsageEstimationGallons : '--'} gal/month</H4>
            </div>
          </div>
        ) : (
          <H4 className="tc">No Data Available for this sensor</H4>
        )}
        <Divider />
        <H3>Historical Data</H3>
        <div className="charts">
          <div className="mb2 flex-ns">
            <TimeRangeSelect
              value={timeRange}
              onChange={this.handleTimeRangeChange}
            />
            {timeRange === CUSTOM_TIME_RANGE && (
              <DateRange
                className="ml4-ns"
                label="Select date range to view Water Monitor readings"
                startValue={startValue}
                endValue={endValue}
                onClose={this.onCloseDateRange}
                shouldLimitRange={
                  !hasRole(ROLES.SUPER_ADMIN, ROLES.PILLAR_ADMIN)
                }
              />
            )}
            <Select
              className="ml4-ns"
              label="Smoothing Interval"
              input={{
                value: smoothInterval,
                onChange: x => this.handleFilterChange(x),
              }}
            >
              {Object.keys(SMOOTH_INTERVAL).map(x => (
                <Option value={SMOOTH_INTERVAL[x]} key={x}>
                  {toTitleCase(x)}
                </Option>
              ))}
            </Select>
          </div>
          {flowMonitorData.data && flowMonitorData.data.length ? (
            <FlowHistoryChart
              data={flowMonitorData.data}
              columns={flowMonitorData.columns}
              updateQueryParams={updateQueryParams}
            />
          ) : (
            <H4 className="tc">No Data Available for Selected Date Range</H4>
          )}
        </div>
      </Fragment>
    )
  }
}

export default FlowMonitorData
